import React from "react";
import type { AppContext, AppProps } from "next/app";
import type { ReactElement, ReactNode } from "react";
import type { NextPage } from "next";
import App from "next/app";
import { Hydrate, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";

import { AuthTokenRefreshProvider } from "@/contexts/AuthTokenRefresh";

import { GTM_ID, pageview } from "@/components/shared/gtm";
import { useRouter } from "next/router";
import { useEffect } from "react";

import "../styles/globals.css";
import "../styles/budget-calculator.css";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/bip-pdf.css";
import Script from "next/script";
import { ModalPopupProvider } from "@/contexts/navigationContext/ModalPopupContext";
import { useAttributionTracker } from "@/shared/hooks/useUTMTracker";
import { useReferralTracker } from "@/shared/hooks/useReferralTracker";
import axiosInstance, { getAuthUser } from "@/services/api";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { queryClient } from "@/services/queryClient";

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page) => page);

  const router = useRouter();

  useEffect(() => {
    router.events.on("routeChangeComplete", pageview);
    return () => {
      router.events.off("routeChangeComplete", pageview);
    };
  }, [router.events]);

  const completeLearnSisterBrandDailyTask = async (userId: string) => {
    await axiosInstance.post(
      `/users/${userId}/daily-challenges/events/complete-learn-sister-brand-challenge`,
    );
  };

  useEffect(() => {
    if (router.query.utm_source && router.query.utm_source === "klinkdailytask") {
      const userId = router.query.userId;
      completeLearnSisterBrandDailyTask(userId as string);
    }
  }, []);

  // Capture UTMs
  useAttributionTracker();
  useReferralTracker();

  return (
    <QueryClientProvider client={queryClient}>
      <Hydrate state={pageProps.dehydratedState}>
        <AuthTokenRefreshProvider initialExpiresAt={null}>
          <ModalPopupProvider>
            <Script
              id="gtm"
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer', '${GTM_ID}');
          `,
              }}
            />
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              draggable
              pauseOnHover
              theme="light"
            />
            {getLayout(<Component {...pageProps} />)}
          </ModalPopupProvider>
        </AuthTokenRefreshProvider>
        <ReactQueryDevtools />
      </Hydrate>
    </QueryClientProvider>
  );
}

MyApp.getInitialProps = async (context: AppContext) => {
  const appProps = await App.getInitialProps(context);
  return { ...appProps };
};

export default MyApp;
